import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { ToastService } from '../services/toast.service';

type ProblemDetails = {
  title?: string;
  detail?: string;
  status?: number;
  instance?: string;
};

/**
 * Handles non-auth HTTP errors.
 *
 * Responsibilities:
 * - leaves `401` handling to `authInterceptor`
 * - shows consistent toast messages for network, transient, server, and problem-details errors
 * - always rethrows the original error
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const notifyAndRethrow = (error: HttpErrorResponse, message: string) => {
    toastService.error(message);
    return throwError(() => error);
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Let the auth interceptor own 401 behavior.
      if (error.status === 401) {
        return throwError(() => error);
      }

      if (error.status === 0) {
        return notifyAndRethrow(error, 'Network error. Please check your connection and try again.');
      }

      if ([408, 429, 503, 504].includes(error.status)) {
        return notifyAndRethrow(error, 'Service is temporarily unavailable. Please try again shortly.');
      }

      if (error.status >= 500) {
        return notifyAndRethrow(error, 'Server error. Please try again later.');
      }

      const problem = (typeof error.error === 'object' ? (error.error as ProblemDetails) : undefined);
      if (problem?.title || problem?.detail) {
        return notifyAndRethrow(error, problem.detail || problem.title || 'Request failed.');
      }

      return throwError(() => error);
    }),
  );
};
